import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as Diff from 'diff';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import axios from 'axios';

import GetCookie from '../../utils/GetCookie';
import { useTimezone } from '../../utils/TimezoneContext';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import ErrorPopup from '../../utils/popup/ErrorPopup';

import GraphComponent from '../copilotComponents/GraphComponent';
import TableComponent from '../copilotComponents/TableComponent';
import ImageComponent from '../copilotComponents/ImageComponent';
import ReferencesComponent from '../copilotComponents/ReferencesComponent';


const MIN_PANEL_WIDTH = 400;
const MAX_PANEL_WIDTH = 900;
const DEFAULT_PANEL_WIDTH = 500;


const truncate = (str, max = 15) => {
  const s = (str || '').trim();
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + '…';
};

const trimDocTagSpans = (md, maxLen = 15) => {
  if (!md) return md;
  return md.replace(
    /<span([^>]*\b(?:ev-message-card6|ev-message-card7)\b[^>]*)>([\s\S]*?)<\/span>/g,
    (full, attrs, inner) => {
      const rawText = (inner || '').trim();
      if (rawText.includes('<')) return full;
      const shortText = truncate(rawText, maxLen);
      const hasTitle = /\stitle=/.test(attrs);
      const safeTitle = rawText.replace(/"/g, '&quot;');
      const newAttrs = hasTitle ? attrs : `${attrs} title="${safeTitle}"`;
      return `<span${newAttrs}>${shortText}</span>`;
    }
  );
};

const processMarkdownForDisplay = (response) => {
  if (!response || !response.markdown) return '';

  const graphs = response.graphs || [];
  const images = response.images || [];
  const tables = response.tables || [];

  const graphMap = {};
  const imageMap = {};
  const tableMap = {};

  graphs.forEach((g, i) => { graphMap[i + 1] = g.graph_number ?? (i + 1); });
  images.forEach((im, i) => { imageMap[i + 1] = im.img_num ?? (i + 1); });
  tables.forEach((t, i) => { tableMap[i + 1] = t.table_number ?? (i + 1); });

  let md = response.markdown;

  md = md.replace(/\[(graph|image|table)_(\d+)\]/g, (match, type, idxStr) => {
    const idx = Number(idxStr);
    if (type === 'graph' && graphMap[idx]) return `Figure ${graphMap[idx]}`;
    if (type === 'image' && imageMap[idx]) return `Figure ${imageMap[idx]}`;
    if (type === 'table' && tableMap[idx]) return `Table ${tableMap[idx]}`;
    return match;
  });

  md = trimDocTagSpans(md, 15);
  return md;
};

const tokenizeHtmlSpans = (md) => {
  if (!md) return { tokenized: '', map: {} };
  const map = {};
  let counter = 0;
  const tokenized = md.replace(/<span[^>]*>[\s\S]*?<\/span>/g, (match) => {
    counter++;
    const token = `⟦S${counter}⟧`;
    map[token] = match;
    return token;
  });
  return { tokenized, map };
};

const restoreSpans = (md, ...maps) => {
  let result = md;
  for (const map of maps) {
    for (const [token, original] of Object.entries(map)) {
      result = result.split(token).join(original);
    }
  }
  return result;
};

// Wrap a diff chunk in <mark> while keeping newlines OUTSIDE the tag.
// Without this, added/removed content that spans paragraphs ends up with
// <mark> tags straddling markdown block boundaries — rehype-raw then either
// strips the tag or renders it as an empty inline, so the highlight disappears.
// We split on newline runs and wrap only the non-empty segments between them.
const wrapDiffChunk = (value, klass) => {
  if (!value) return '';
  // Split but KEEP the newline separators as their own array entries.
  const segments = value.split(/(\n+)/);
  return segments
    .map(seg => {
      if (!seg) return '';
      if (/^\n+$/.test(seg)) return seg; // raw newlines pass through
      return `<mark class="${klass}">${seg}</mark>`;
    })
    .join('');
};

const computeDiffedMarkdowns = (oldMd, newMd) => {
  if (!oldMd && !newMd) return { left: '', right: '', identical: true };
  if (!oldMd) return { left: '', right: newMd, identical: false };
  if (!newMd) return { left: oldMd, right: '', identical: false };
  if (oldMd === newMd) return { left: oldMd, right: newMd, identical: true };

  const { tokenized: tokOld, map: mapOld } = tokenizeHtmlSpans(oldMd);
  const { tokenized: tokNew, map: mapNew } = tokenizeHtmlSpans(newMd);

  const changes = Diff.diffWords(tokOld, tokNew);

  const leftParts = [];
  const rightParts = [];

  changes.forEach(part => {
    if (part.added) {
      rightParts.push(wrapDiffChunk(part.value, 'diff-word-added'));
    } else if (part.removed) {
      leftParts.push(wrapDiffChunk(part.value, 'diff-word-removed'));
    } else {
      leftParts.push(part.value);
      rightParts.push(part.value);
    }
  });

  const left = restoreSpans(leftParts.join(''), mapOld, mapNew);
  const right = restoreSpans(rightParts.join(''), mapOld, mapNew);

  return { left, right, identical: false };
};

const hasMeaningfulReferences = (refs) => {
  if (!refs) return false;
  if (Array.isArray(refs)) return refs.length > 0;
  if (typeof refs === 'object') {
    return (refs.doc_ids && refs.doc_ids.length > 0) ||
      (refs.ar_ids && refs.ar_ids.length > 0);
  }
  return false;
};


const normalizeCellText = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
};

const normalizeCaption = (caption) => {
  if (!caption || typeof caption !== 'string') return '';
  return caption.toLowerCase().replace(/\s+/g, ' ').trim();
};

const pairTablesByCaption = (leftTables = [], rightTables = []) => {
  const getNum = (t, idx) => t.table_number ?? (idx + 1);

  const leftMatched = new Set();
  const rightMatched = new Set();
  const pairs = [];

  const rightCaptionIndex = new Map();
  rightTables.forEach((t, i) => {
    const cap = normalizeCaption(t.subtitle);
    if (cap && !rightCaptionIndex.has(cap)) {
      rightCaptionIndex.set(cap, i);
    }
  });

  leftTables.forEach((leftTable, leftIdx) => {
    const cap = normalizeCaption(leftTable.subtitle);
    if (!cap) return;
    const rightIdx = rightCaptionIndex.get(cap);
    if (rightIdx === undefined || rightMatched.has(rightIdx)) return;
    const rightTable = rightTables[rightIdx];
    leftMatched.add(leftIdx);
    rightMatched.add(rightIdx);
    pairs.push({
      tableNumber: getNum(rightTable, rightIdx),
      left: leftTable,
      right: rightTable,
    });
  });

  leftTables.forEach((leftTable, leftIdx) => {
    if (leftMatched.has(leftIdx)) return;
    pairs.push({
      tableNumber: getNum(leftTable, leftIdx),
      left: leftTable,
      right: null,
    });
  });

  rightTables.forEach((rightTable, rightIdx) => {
    if (rightMatched.has(rightIdx)) return;
    pairs.push({
      tableNumber: getNum(rightTable, rightIdx),
      left: null,
      right: rightTable,
    });
  });

  pairs.sort((a, b) => a.tableNumber - b.tableNumber);

  return pairs.map(({ tableNumber, left, right }) => {
    let status;
    if (left && !right) status = 'removed';
    else if (!left && right) status = 'added';
    else {
      const leftText = normalizeCellText(left.html);
      const rightText = normalizeCellText(right.html);
      status = leftText === rightText ? 'unchanged' : 'modified';
    }
    return { tableNumber, left, right, status };
  });
};

const computeTableCellDiff = (leftHtml, rightHtml) => {
  if (!leftHtml || !rightHtml) {
    return { leftHtml: leftHtml || '', rightHtml: rightHtml || '' };
  }

  const parser = new DOMParser();
  const leftDoc = parser.parseFromString(leftHtml, 'text/html');
  const rightDoc = parser.parseFromString(rightHtml, 'text/html');

  const leftTable = leftDoc.querySelector('table');
  const rightTable = rightDoc.querySelector('table');
  if (!leftTable || !rightTable) {
    return { leftHtml, rightHtml };
  }

  const leftRows = Array.from(leftTable.querySelectorAll('tr'));
  const rightRows = Array.from(rightTable.querySelectorAll('tr'));
  const maxRows = Math.max(leftRows.length, rightRows.length);

  const wrapCell = (cell, klass) => {
    if (!cell) return;
    cell.innerHTML = `<mark class="${klass}">${cell.innerHTML}</mark>`;
  };

  for (let r = 0; r < maxRows; r++) {
    const lRow = leftRows[r];
    const rRow = rightRows[r];

    if (lRow && !rRow) {
      lRow.querySelectorAll('td, th').forEach(c => wrapCell(c, 'diff-word-removed'));
      continue;
    }
    if (!lRow && rRow) {
      rRow.querySelectorAll('td, th').forEach(c => wrapCell(c, 'diff-word-added'));
      continue;
    }

    const lCells = Array.from(lRow.querySelectorAll('td, th'));
    const rCells = Array.from(rRow.querySelectorAll('td, th'));
    const maxCells = Math.max(lCells.length, rCells.length);

    for (let c = 0; c < maxCells; c++) {
      const lCell = lCells[c];
      const rCell = rCells[c];

      if (lCell && !rCell) {
        wrapCell(lCell, 'diff-word-removed');
      } else if (!lCell && rCell) {
        wrapCell(rCell, 'diff-word-added');
      } else if (lCell && rCell) {
        const lText = normalizeCellText(lCell.innerHTML);
        const rText = normalizeCellText(rCell.innerHTML);
        if (lText !== rText) {
          wrapCell(lCell, 'diff-word-removed');
          wrapCell(rCell, 'diff-word-added');
        }
      }
    }
  }

  return {
    leftHtml: leftTable.outerHTML,
    rightHtml: rightTable.outerHTML,
  };
};


const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <div className="ev-med-p-blue mb-2">{children}</div>,
  h2: ({ children }) => <div className="ev-reg-p-dark mb-1 fw-bold">{children}</div>,
  h3: ({ children }) => <div className="ev-reg-p-dark mb-1 fw-bold">{children}</div>,
  h4: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
  h5: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
  h6: ({ children }) => <div className="ev-small-p-blue mb-1 fw-bold">{children}</div>,
};

const VersionMetadataStrip = ({ payload }) => {
  const { formatDateTime } = useTimezone();
  if (!payload) {
    return (
      <div className="agent-version-compare-meta-half">
        <span className="ev-xsmall-p-dark" style={{ opacity: 0.5 }}>—</span>
      </div>
    );
  }
  const { version, source, timestamp } = payload;
  return (
    <div className="agent-version-compare-meta-half d-flex align-items-center gap-2 flex-wrap">
      <span className="ev-bold-med ev-reg-p-dark-blue">
        Version {version}
      </span>
      {/* {source && (
        <span className={`ev-btn-xsmall ${source === 'manual' ? 'genbi-source-manual' : 'genbi-source-ai'}`}>
          {source === 'manual' ? (
            <><i className="bi bi-person-fill me-1 ev-bold-reg"></i>Manual</>
          ) : (
            <><i className="bi bi-stars me-1 ev-bold-reg"></i>AI</>
          )}
        </span>
      )} */}
      {timestamp && (
        <span className="agent-version-compare-timestamp ev-xsmall-p-dark">
          {formatDateTime(timestamp)}
        </span>
      )}
    </div>
  );
};

const VersionColumn = ({ payload, annotatedMarkdown, annotatedTables }) => {
  if (!payload || !payload.response) {
    return (
      <div className="agent-version-compare-column d-flex align-items-center justify-content-center">
        <span className="ev-xsmall-p-dark" style={{ opacity: 0.5 }}>No data</span>
      </div>
    );
  }

  const { response } = payload;
  const inPlaceGraphMap = {};
  (response.in_place_graphs || []).forEach(ipg => {
    if (ipg?.index != null) inPlaceGraphMap[ipg.index] = ipg;
  });
  const graphs = (response.graphs || []).map((g, i) => ({
    ...g, figureNumber: g.graph_number ?? (i + 1),
  }));
  const images = (response.images || []).map((im, i) => ({
    ...im, figureNumber: im.img_num ?? (i + 1),
  }));
  const refs = response.references;
  const showRefs = hasMeaningfulReferences(refs);
  const hasTables = annotatedTables && annotatedTables.length > 0;

  return (
    <div className="agent-version-compare-column scrollable-element">
      {annotatedMarkdown && (
        <div className="markdown-justified mb-3 ev-small-p-dark p-3">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={MARKDOWN_COMPONENTS}
          >
            {annotatedMarkdown}
          </ReactMarkdown>
        </div>
      )}

      {graphs.length > 0 && (
        <div className="mb-3 px-3">
          {graphs.map(graph => (
            <GraphComponent
              key={graph.graph_id || graph.figureNumber}
              graphData={graph}
              isLoading={false}
              contentId={graph.figureNumber}
              subtitle={graph.subtitle}
              footer={'footer' in graph ? graph.footer : null}
            />
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div className="mb-3 px-3">
          {images.map(image => (
            <ImageComponent
              key={image.image_id || image.figureNumber}
              imageData={image}
              isLoading={false}
              contentId={image.figureNumber}
              subtitle={image.subtitle}
              footer={'footer' in image ? image.footer : null}
            />
          ))}
        </div>
      )}

      {hasTables && (
        <div className="mb-3 px-3">
          {annotatedTables.map(item => {
            const { status, tableData, tableNumber, badgeLabel } = item;
            const wrapperClass =
              status === 'added' ? 'agent-version-compare-asset agent-version-compare-asset--added pb-0' :
                status === 'removed' ? 'agent-version-compare-asset agent-version-compare-asset--removed pb-0' :
                  status === 'modified' ? 'agent-version-compare-asset agent-version-compare-asset--modified pb-0' :
                    'agent-version-compare-asset';
            const badgeClass =
              status === 'added' ? 'agent-version-compare-asset-badge agent-version-compare-asset-badge--added' :
                status === 'removed' ? 'agent-version-compare-asset-badge agent-version-compare-asset-badge--removed' :
                  status === 'modified' ? 'agent-version-compare-asset-badge agent-version-compare-asset-badge--modified' :
                    null;
            return (
              <div key={`${tableNumber}-${status}`} className={wrapperClass}>
                {badgeClass && (
                  <span className={badgeClass}>
                    <i className={`bi ${status === 'added' ? 'bi-plus-circle' :
                        status === 'removed' ? 'bi-dash-circle' :
                          'bi-pencil-square'
                      } me-1`}></i>
                    {badgeLabel}
                  </span>
                )}
                <TableComponent
                  tableData={tableData}
                  isLoading={false}
                  contentId={tableNumber}
                  subtitle={tableData.subtitle}
                  inPlaceGraphMap={inPlaceGraphMap}
                />
              </div>
            );
          })}
        </div>
      )}

      {showRefs && (
        <div className="px-3 pb-3">
          <ReferencesComponent references={refs} />
        </div>
      )}

      {!annotatedMarkdown && graphs.length === 0 && images.length === 0 && !hasTables && !showRefs && (
        <div className="text-center p-4">
          <span className="ev-small-p-dark" style={{ opacity: 0.5 }}>
            No content in this version.
          </span>
        </div>
      )}
    </div>
  );
};


const AgentVersionComparePanel = ({ agentId, versionA, versionB, fabricId, onClose }) => {
  const [data, setData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('Loading version comparison...');
  const [errorMessage, setErrorMessage] = useState('');

  // ── Panel width drag state (left-edge resize) ──
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const isDraggingWidth = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const panelRef = useRef(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const fetchComparison = async () => {
      setIsProcessing(true);
      setProcessingMessage('Loading version comparison...');
      try {
        const API_BASE_URL = '/pceapi/genbi';
        const headers = {
          Authorization: GetCookie('access_token'),
        };

        const response = await axios.post(
          `${API_BASE_URL}/compare-agent-versions`,
          {
            agent_id: agentId,
            version_a: versionA,
            version_b: versionB,
            fabric_id: fabricId,
          },
          { headers }
        );
        if (cancelled) return;
        if (response.data.status === 'success' && response.data.data) {
          setData(response.data.data);
        } else {
          setErrorMessage(response.data.message || 'Failed to load version comparison.');
        }
      } catch (e) {
        if (!cancelled) setErrorMessage('Network error: ' + e.message);
      } finally {
        if (!cancelled) setIsProcessing(false);
      }
    };
    fetchComparison();
    return () => { cancelled = true; };
  }, [agentId, versionA, versionB, fabricId]);

  // ── ESC to close ──
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current && onCloseRef.current();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // ── Click outside to close ──
  useEffect(() => {
    const handler = (e) => {
      if (!document.contains(e.target)) return;
      if (e.target.closest('.modal') || e.target.closest('.react-doc-viewer')) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onCloseRef.current && onCloseRef.current();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Left-edge drag resize ──
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingWidth.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingWidth.current) return;
      // Dragging left increases width (panel is right-anchored)
      const delta = dragStartX.current - e.clientX;
      const newW = Math.min(
        MAX_PANEL_WIDTH,
        Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + delta)
      );
      setPanelWidth(newW);
    };
    const onUp = () => {
      if (!isDraggingWidth.current) return;
      isDraggingWidth.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const closeErrorPopup = () => {
    setErrorMessage('');
    onClose();
  };

  // ── Defensive ordering: older on left, newer on right ──
  let leftPayload = data?.version_a || null;
  let rightPayload = data?.version_b || null;
  if (
    leftPayload && rightPayload &&
    typeof leftPayload.version === 'number' &&
    typeof rightPayload.version === 'number' &&
    leftPayload.version > rightPayload.version
  ) {
    [leftPayload, rightPayload] = [rightPayload, leftPayload];
  }

  const { left: leftMd, right: rightMd, identical: isTextIdentical } = useMemo(() => {
    if (!leftPayload && !rightPayload) return { left: '', right: '', identical: true };
    const oldMd = processMarkdownForDisplay(leftPayload?.response);
    const newMd = processMarkdownForDisplay(rightPayload?.response);
    return computeDiffedMarkdowns(oldMd, newMd);
  }, [leftPayload, rightPayload]);

  const { leftAnnotatedTables, rightAnnotatedTables, allTablesUnchanged } = useMemo(() => {
    const leftTables = (leftPayload?.response?.tables || []).map(t => ({
      ...t,
      html: t.html ? trimDocTagSpans(t.html, 15) : t.html,
    }));
    const rightTables = (rightPayload?.response?.tables || []).map(t => ({
      ...t,
      html: t.html ? trimDocTagSpans(t.html, 15) : t.html,
    }));

    const pairs = pairTablesByCaption(leftTables, rightTables);

    const leftAnnotated = [];
    const rightAnnotated = [];
    let allUnchanged = true;

    pairs.forEach(({ tableNumber, left, right, status }) => {
      if (status !== 'unchanged') allUnchanged = false;

      if (status === 'unchanged') {
        leftAnnotated.push({ status, tableNumber, tableData: left, badgeLabel: null });
        rightAnnotated.push({ status, tableNumber, tableData: right, badgeLabel: null });
      } else if (status === 'removed') {
        leftAnnotated.push({
          status, tableNumber, tableData: left,
          badgeLabel: 'Removed in new version',
        });
      } else if (status === 'added') {
        rightAnnotated.push({
          status, tableNumber, tableData: right,
          badgeLabel: 'Added in new version',
        });
      } else if (status === 'modified') {
        const { leftHtml, rightHtml } = computeTableCellDiff(left.html, right.html);
        leftAnnotated.push({
          status, tableNumber,
          tableData: { ...left, html: leftHtml },
          badgeLabel: 'Modified',
        });
        rightAnnotated.push({
          status, tableNumber,
          tableData: { ...right, html: rightHtml },
          badgeLabel: 'Modified',
        });
      }
    });

    return {
      leftAnnotatedTables: leftAnnotated,
      rightAnnotatedTables: rightAnnotated,
      allTablesUnchanged: allUnchanged,
    };
  }, [leftPayload, rightPayload]);

  return (
    <>
      {isProcessing && <ProcessingPopup message={processingMessage} />}
      {errorMessage && <ErrorPopup message={errorMessage} onClose={closeErrorPopup} />}

      <div
        ref={panelRef}
        className="agent-version-compare-panel position-absolute ev-message-card2 ev-card-3 ev-radius-reg shadow d-flex flex-column vh-80"
        style={{ width: panelWidth }}
        role="dialog"
        aria-label="Agent version comparison"
      >
        {/* ── Left-edge drag handle ── */}
        <div
          className="agent-version-compare-drag-handle"
          onMouseDown={handleDragStart}
          title="Drag to resize"
        >
          <div className="agent-version-compare-drag-bar" />
        </div>

        {/* ── Header ── */}
        <div className="agent-version-compare-header d-flex align-items-center justify-content-between flex-shrink-0">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-files ev-small-p-blue"></i>
            <span className="ev-bold-med ev-reg-p-dark">Version Comparison</span>
            {leftPayload && rightPayload && (
              <span className="ev-message-card1 ev-btn-xsmall ms-1">
                <i className="bi bi-arrow-left-right me-1"></i>
                v{leftPayload.version}&nbsp;vs&nbsp;v{rightPayload.version}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {data && (!isTextIdentical || !allTablesUnchanged) && (
              <div className="d-flex align-items-center gap-2 me-2">
                <span className="d-inline-flex align-items-center gap-1">
                  <span className="agent-version-compare-legend agent-version-compare-legend--removed"></span>
                  <span className="ev-xsmall-p-dark">Removed</span>
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  <span className="agent-version-compare-legend agent-version-compare-legend--added"></span>
                  <span className="ev-xsmall-p-dark">Added</span>
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  <span className="agent-version-compare-legend agent-version-compare-legend--modified"></span>
                  <span className="ev-xsmall-p-dark">Modified</span>
                </span>
              </div>
            )}
            <button
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall ev-radius-xlarge"
              onClick={onClose}
              aria-label="Close comparison"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* ── Metadata stripe ── */}
        <div className="agent-version-compare-meta-bar d-flex flex-shrink-0">
          <VersionMetadataStrip payload={leftPayload} />
          <div className="agent-version-compare-meta-divider" />
          <VersionMetadataStrip payload={rightPayload} />
        </div>

        {/* ── Identical banner ── */}
        {data && isTextIdentical && allTablesUnchanged && (
          <div className="agent-version-compare-identical flex-shrink-0">
            <i className="bi bi-check2-circle me-2"></i>
            Content is identical between these versions.
          </div>
        )}

        {/* ── Side-by-side columns ── */}
        <div className="agent-version-compare-columns flex-grow-1">
          {!data && !isProcessing && !errorMessage && (
            <div className="d-flex align-items-center justify-content-center w-100 p-4">
              <span className="ev-small-p-dark" style={{ opacity: 0.5 }}>Loading…</span>
            </div>
          )}
          {data && (
            <>
              <VersionColumn
                payload={leftPayload}
                annotatedMarkdown={leftMd}
                annotatedTables={leftAnnotatedTables}
              />
              <div className="agent-version-compare-column-divider" />
              <VersionColumn
                payload={rightPayload}
                annotatedMarkdown={rightMd}
                annotatedTables={rightAnnotatedTables}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AgentVersionComparePanel;